<template>
    <div>
        <!-- Filters in white card -->
        <div class="mb-2 p-2">
            <div class="col-sm-12 search-form-title">
                <font-awesome-icon icon="search" />
                {{ $t('common.search') }}
            </div>
            <div class="row">
                <div class="col-sm-6">
                    <label class="small"><b>{{ $t('invoice_history.start_date') }}</b></label>
                    <input type="date" class="form-control form-control-sm" v-model="startDate" />
                </div>
                <div class="col-sm-6">
                    <label class="small"><b>{{ $t('invoice_history.end_date') }}</b></label>
                    <input type="date" class="form-control form-control-sm" v-model="endDate" />
                </div>
            </div>
            <div class="row">
                <div class="col-sm-6">
                    <label class="small"><b>{{ $t('invoice_history.contract') }}</b></label>
                    <open-contract-filter
                        :from="startDate"
                        :to="endDate"
                        :show-company="false"
                        :show-title="false"
                        :showOnlyMyContractsOption="true"
                        @contractSelected="setContract"
                    />
                </div>
                <div class="col-sm-6">
                    <label class="small"><b>{{ $t('invoice_history.status') }}</b></label>
                    <select class="form-control form-control-sm" v-model="status">
                        <option value="">{{ $t('invoice_history.all') }}</option>
                        <option :value="2">{{ $t('invoice_history.in_progress') }}</option>
                        <option :value="3">{{ $t('invoice_history.paid') }}</option>
                        <option :value="4">{{ $t('invoice_history.rejected') }}</option>
                    </select>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-6">
                    <label class="small"><b>{{ $t('invoice_history.invoice_number') }}</b></label>
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="invoiceNumber"
                        :placeholder="$t('invoice_history.invoice_number')"
                    />
                </div>
            </div>
            <div class="col-sm-12 button-container d-flex justify-content-end">
                <button
                    v-if="contractSystemEnabled"
                    class="btn btn-danger me-2"
                    @click.stop="openContractSanctionEditor"
                >
                    {{ $t('contract_sanction.new_button') }}
                </button>
                <button
                    class="btn btn-primary"
                    :disabled="loading"
                    @click.stop="fetchInvoices"
                >
                    {{ $t('common.search') }}
                </button>
            </div>
        </div>

        <!-- No results -->
        <Transition name="fade">
            <div v-if="!loading && invoices.length < 1 && contractSanctions.length < 1"  class="col-12 text-center" style="padding-top: 20vh; color: #bcbcbc; font-size: 2em; font-weight: 300">
                {{ $t('common.no_results') }}
            </div>
        </Transition>

        <!-- Summary cards (Count, In payment, paid, sum) -->
        <Transition name="fade">
            <invoice-summary v-if="invoices.length > 0" :invoices="invoices" :status="status" />
        </Transition>

        <!-- Invoice results -->
        <Transition name="fade">
            <invoice-list
                :invoices="invoices"
                :contract-sanctions="contractSanctions"
                :is-observer="isObserver"
                :download-loading="downloadLoading"
                :user="user"
                @creditNoteClicked="onCreditNoteClicked"
                @invoiceClicked="onInvoiceClicked"
                @contractSanctionClicked="onContractSanctionClicked"
                @addCreditNote="openCreditNoteEditor"
                @downloadReport="downloadInvoiceReport"
            />
        </Transition>

        <div
            v-if="loading || downloadLoading"
            id="loader"
            class="spinner"
        />

        <base-modal v-if="showCreditNoteEditor" size="xl" :title="$t('credit_note.title')" hide-footer @close="showCreditNoteEditor = false">
            <credit-note-editor :invoice="creditNoteInvoice" :user="user" @close="showCreditNoteEditor = false" @submitted="onCreditNoteSubmitted"/>
        </base-modal>

        <base-modal v-if="showCreditNoteDetail" size="xl" hide-header hide-footer @close="showCreditNoteDetail = false">
            <credit-note-details :invoice="creditNoteInvoice" :credit-note-id="creditNoteDetail" @close="showCreditNoteDetail = false"/>
        </base-modal>

        <base-modal v-if="showInvoiceDetail" size="xl" hide-header hide-footer @close="showInvoiceDetail = false">
            <invoice-details :invoice="invoiceDetail" @close="showInvoiceDetail = false"/>
        </base-modal>

        <base-modal v-if="showContractSanctionEditor" size="xl" :title="$t('contract_sanction.title')" hide-footer @close="showContractSanctionEditor = false">
            <contract-sanction-editor @close="showContractSanctionEditor = false" @submitted="onContractSanctionSubmitted" />
        </base-modal>

        <base-modal v-if="showContractSanctionDetail" size="xl" hide-header hide-footer @close="showContractSanctionDetail = false">
            <contract-sanction-details :contract-sanction="contractSanctionDetail" @close="showContractSanctionDetail = false" />
        </base-modal>
    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import { timeUtils } from '../mixins/TimeUtils'
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import OpenContractFilter from "../contract/OpenContractFilter.vue";
import BaseModal from "@/components/BaseModal.vue";
import CreditNoteEditor from "@/components/invoice/CreditNoteEditor.vue";
import { priceHelper} from "../mixins/PricingMixin.js";
import InvoiceDetails from "./InvoiceDetails.vue";
import CreditNoteDetails from "./CreditNoteDetails.vue";
import {invoicingHelper} from "../mixins/InvoicingMixin.js";
import InvoiceList from "./InvoiceList.vue";
import InvoiceSummary from "./InvoiceSummary.vue";
import { downloadHelper } from '../mixins/DownloadMixin'
import { inject } from 'vue'
import ContractSanctionEditor from "./ContractSanctionEditor.vue";
import ContractSanctionDetails from "./ContractSanctionDetails.vue";
import {userHelper} from "../mixins/UserMixin";

export default {
    name: 'InvoiceHistory',
    components: {
        InvoiceSummary,
        InvoiceList, CreditNoteDetails, InvoiceDetails, CreditNoteEditor, BaseModal, OpenContractFilter, FontAwesomeIcon, Vue3Datatable,
        ContractSanctionEditor, ContractSanctionDetails },
    mixins: [restApi, timeUtils, priceHelper, invoicingHelper, downloadHelper, userHelper],
    props: {
        user: { type: Object, default: null },
        isObserver: { type: Boolean, default: false }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    computed: {
        // Reuses the company's already-fetched api_key_types.contract_system
        // flag (same user object populated by GET user/token/company/{id},
        // see TempOffice.vue's fetchCompanyToken/handleCompanyTokenResponse)
        // instead of making a separate apikey/url network request. API key
        // type 1 ("contract") is the confirmed Ohjas key type for this
        // feature (ROUTA-5584).
        contractSystemEnabled() {
            return this.user ? this.hasContractSystem(this.user) : false
        }
    },
    data() {
        return {
            invoices: [],
            contractSanctions: [],
            loading: false,
            contract: null,
            startDate: '',
            endDate: '',
            status: null,
            invoiceNumber: '',
            selectedInvoice: null,
            showInvoiceDetail: false,
            invoiceDetail: null,
            showCreditNoteEditor: false,
            creditNoteInvoice: null,
            showCreditNoteDetail: false,
            creditNoteDetail: null,
            downloadLoading: false,
            showContractSanctionEditor: false,
            showContractSanctionDetail: false,
            contractSanctionDetail: null,
        }
    },
    mounted() {
        this.startDate = this.timestampToDateInput(this.getMonthStartAsDate().getTime())
        this.endDate = this.timestampToDateInput(new Date().getTime())
    },
    methods: {
        fetchInvoices() {
            this.loading = true
            this.invoices = []
            let end = new Date(this.endDate)
            end.setHours(23)
            end.setMinutes(59)
            end.setSeconds(59)
            this.restFetchParams(this.invoiceUrl, {
                from: this.startDate,
                to: end.toISOString(),
                status: this.status,
                contract: this.contract,
                invoiceNumber: this.invoiceNumber
            }, this.handleResponse)
        },

        openContractSanctionEditor() {
            this.showContractSanctionEditor = true
        },

        onContractSanctionClicked(row) {
            this.contractSanctionDetail = row
            this.showContractSanctionDetail = true
        },

        onContractSanctionSubmitted() {
            this.showContractSanctionEditor = false
            this.fetchInvoices()
        },

        setContract(contract) {
            this.contract = contract ? contract.id : null
        },

        handleResponse(response) {
            this.loading = false
            if (response && response.data) {
                // GET invoice returns Invoices and Contract Sanctions mixed
                // into one array, both mapped to the same InvoiceDTO shape,
                // already ordered by the backend. Each record carries
                // document_type: "invoice" or "contract_sanction" (confirmed
                // with backend, ROUTA-5584) - split them here so InvoiceList
                // can render sanctions as their own top-level rows, but tag
                // each record with its original backend order first so the
                // two lists can be merged back into that same order instead
                // of sanctions being appended after all invoices. Records
                // with a missing/null document_type (older data) are
                // treated as invoices.
                const records = Array.isArray(response.data) ? response.data : (response.data.invoices || [])
                const ordered = records.map((r, index) => ({ ...r, _order: index }))
                this.invoices = ordered.filter(r => r.document_type !== 'contract_sanction')
                this.contractSanctions = ordered.filter(r => r.document_type === 'contract_sanction')
            }        },

        onInvoiceClicked(invoice) {
            this.invoiceDetail = this.invoices.find(i => i.id === invoice.id)
            if (this.invoiceDetail) {
                this.showInvoiceDetail = true
            }
        },

        onCreditNoteClicked(creditNote) {
            this.creditNoteDetail = creditNote?.id
            this.creditNoteInvoice = this.invoices.find(invoice => invoice.id === creditNote.invoice_id)
            if (this.creditNoteDetail && this.creditNoteInvoice) {
                this.showCreditNoteDetail = true
            }
        },

        openCreditNoteEditor(invoice) {
            this.creditNoteInvoice = this.invoices.find(inv => inv.id === invoice.id)
            if (this.creditNoteInvoice) this.showCreditNoteEditor = true
        },

        onCreditNoteSubmitted() {
            this.showCreditNoteEditor = false
            this.fetchInvoices()
        },

        buildInvoiceReportParams() {
            const params = {}
            if (this.contract !== null && this.contract !== undefined && this.contract !== '') {
                params.contract = this.contract
            }
            const from = this.startDate
            if (from && !isNaN(new Date(from).getTime())) {
                params.from = from
            }
            const to = this.endDate
            if (to && !isNaN(new Date(to).getTime())) {
                const end = new Date(to)
                end.setHours(23)
                end.setMinutes(59)
                end.setSeconds(59)
                params.to = end.toISOString()
            }
            if (this.status !== null && this.status !== undefined && this.status !== '') {
                params.status = this.status
            }
            return this.getCommonReportParams(params)
        },

        downloadInvoiceReport() {
            if (this.loading || this.downloadLoading) {
                return
            }
            this.downloadLoading = true
            this.restGetDownloadFile(
                this.invoiceReportExcelUrl,
                this.buildInvoiceReportParams(),
                this.invoiceReportDownloadSuccess,
                this.invoiceReportDownloadFail
            )
        },

        invoiceReportDownloadSuccess(response) {
            this.downloadLoading = false
            this.downloadFile(response, this.$t('invoice_history.report_file_name'))
        },

        invoiceReportDownloadFail() {
            this.downloadLoading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        },
    }
}
</script>

<style scoped>
</style>
